import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, FloppyDisk, ListChecks, X, Tag } from "@phosphor-icons/react";

/**
 * Gestión de subencargos y tipos de encargo custom por aseguradora.
 * - Subencargos: tipo + subtipo que aplican solo a Videoperitación.
 * - Tipos de encargo custom: se añaden al desplegable Tipo encargo cuando
 *   el expediente es de esta aseguradora (ej. Mapfre: Consulta, Retomar).
 */
export default function SubencargosTab({ aseguradoraId, canEdit }) {
  return (
    <div className="space-y-10 max-w-3xl">
      <TiposEncargoCustomBlock aseguradoraId={aseguradoraId} canEdit={canEdit} />
      <SubencargosBlock aseguradoraId={aseguradoraId} canEdit={canEdit} />
    </div>
  );
}

// =====================================================================
// Tipos de encargo custom
// =====================================================================

function TiposEncargoCustomBlock({ aseguradoraId, canEdit }) {
  const [items, setItems] = useState([]);
  const [newTipo, setNewTipo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/aseguradoras/${aseguradoraId}/tipos-encargo-custom`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [aseguradoraId]);

  const persist = async (next) => {
    setSaving(true); setErr("");
    try {
      await api.put(`/aseguradoras/${aseguradoraId}/tipos-encargo-custom`, { items: next });
      setItems(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const add = async (e) => {
    e?.preventDefault?.();
    const t = newTipo.trim();
    if (!t) return;
    if (items.includes(t)) { setNewTipo(""); return; }
    await persist([...items, t]);
    setNewTipo("");
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar "${t}"?`)) return;
    await persist(items.filter((x) => x !== t));
  };

  return (
    <section>
      <header className="mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted font-bold">
          <Tag className="w-4 h-4" weight="duotone" />
          Tipos de encargo exclusivos
        </div>
        <h3 className="font-display text-lg font-bold tracking-tight">
          Tipos que solo aparecen para esta aseguradora
        </h3>
        <p className="text-[12px] text-muted">
          Se añaden al desplegable <b>Tipo encargo</b> de la pestaña Datos del
          expediente. Ej. Mapfre usa "Consulta" y "Retomar".
        </p>
      </header>

      {err && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2 text-[12px] mb-3">
          {err}
        </div>
      )}

      {canEdit && (
        <form onSubmit={add} className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={newTipo}
            onChange={(e) => setNewTipo(e.target.value)}
            placeholder="Ej. Consulta"
            data-testid="tipo-custom-input"
            className="text-[12px] px-2 py-1.5 border border-border rounded-md flex-1 max-w-xs"
          />
          <button
            type="submit"
            disabled={!newTipo.trim() || saving}
            data-testid="tipo-custom-add"
            className="inline-flex items-center gap-1 bg-foreground text-white text-[11px] font-semibold rounded-md px-2.5 py-1.5 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir
          </button>
        </form>
      )}

      {items.length === 0 ? (
        <div className="text-[12px] text-muted italic">
          Sin tipos exclusivos. Esta aseguradora usa solo los tipos estándar.
        </div>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {items.map((t) => (
            <li
              key={t}
              data-testid={`tipo-custom-${t}`}
              className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-900 border border-amber-200 rounded-full pl-3 pr-1.5 py-0.5 text-[12px] font-medium"
            >
              {t}
              {canEdit && (
                <button
                  onClick={() => remove(t)}
                  title="Eliminar"
                  className="hover:bg-amber-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" weight="bold" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// =====================================================================
// Subencargos (solo para tipos Videoperitación)
// =====================================================================

function SubencargosBlock({ aseguradoraId, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [draft, setDraft] = useState({ tipo: "", subtipo: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/aseguradoras/${aseguradoraId}/subencargos`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [aseguradoraId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.tipo.trim()) {
      setErr("El campo Tipo es obligatorio");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/aseguradoras/${aseguradoraId}/subencargos`, {
        tipo: draft.tipo.trim(),
        subtipo: draft.subtipo.trim() || null,
      });
      setDraft({ tipo: "", subtipo: "" });
      setAdding(false);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Error al añadir");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sub) => {
    if (!window.confirm(`¿Eliminar "${sub.tipo}${sub.subtipo ? " · " + sub.subtipo : ""}"?`)) return;
    try {
      await api.delete(`/aseguradoras/${aseguradoraId}/subencargos/${sub.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <section>
      <header className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted font-bold">
            <ListChecks className="w-4 h-4" weight="duotone" />
            Subencargos
          </div>
          <h3 className="font-display text-lg font-bold tracking-tight">
            Para tipos Videoperitación
          </h3>
          <p className="text-[12px] text-muted">
            En la pestaña Datos del expediente aparecerá un selector con esta
            lista cuando el Tipo encargo sea <b>Videoperitación</b> y la
            aseguradora coincida.
          </p>
        </div>
        {canEdit && !adding && (
          <button
            onClick={() => setAdding(true)}
            data-testid="subenc-add"
            className="inline-flex items-center gap-1 bg-foreground text-white text-[11px] font-semibold rounded-md px-2.5 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo subencargo
          </button>
        )}
      </header>

      {err && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2 text-[12px]">
          {err}
        </div>
      )}

      {adding && (
        <form onSubmit={submit} className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-[12px] space-y-1">
              <span className="font-semibold">Tipo *</span>
              <input
                type="text"
                value={draft.tipo}
                onChange={(e) => setDraft((d) => ({ ...d, tipo: e.target.value }))}
                placeholder="Ej. Videoperitación asegurado"
                autoFocus
                required
                data-testid="subenc-tipo"
                className="w-full"
              />
            </label>
            <label className="text-[12px] space-y-1">
              <span className="font-semibold">Subtipo (opcional)</span>
              <input
                type="text"
                value={draft.subtipo}
                onChange={(e) => setDraft((d) => ({ ...d, subtipo: e.target.value }))}
                placeholder="Ej. >1000 €"
                data-testid="subenc-subtipo"
                className="w-full"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="subenc-save"
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-md px-3 py-1.5 disabled:opacity-50"
            >
              <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
              {busy ? "Guardando..." : "Guardar"}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setDraft({ tipo: "", subtipo: "" }); setErr(""); }}
              className="text-[11px] text-muted hover:text-foreground inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" weight="bold" /> Cancelar
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-10 text-muted text-[12px]">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-[12px] text-muted">
          Aún no hay subencargos. Pulsa <b>Nuevo subencargo</b> para crear el primero.
        </div>
      ) : (
        <ul className="bg-white border border-border rounded-lg divide-y divide-slate-100">
          {items.map((s) => (
            <li
              key={s.id}
              className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/60"
              data-testid={`subenc-row-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 truncate">{s.tipo}</div>
                {s.subtipo && (
                  <div className="text-[11px] text-muted truncate">{s.subtipo}</div>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => remove(s)}
                  data-testid={`subenc-del-${s.id}`}
                  title="Eliminar"
                  className="text-muted hover:text-red-600 p-1"
                >
                  <Trash className="w-3.5 h-3.5" weight="bold" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
